<template>
  <div>
    <MainHeader />

    <SubHeader
      v-bind:factsheet="factsheet"
      v-bind:loaded="loaded"
      v-bind:location="location"
    />

    <div class="factsheet">
      <!--
            Generalized component for all factsheets. 

            Manages loading of data, status display (expired, errors, etc) and
            calls the subcomponent for the actual factsheet.

            Todo: Clean this up a little bit.
        -->
      <div class="container">
        <div class="row">
          <div class="col" style="padding: 0px">
            <div class="factsheet-status-error" v-if="factsheet_status == 404">
              <p><strong>This factsheet could not be found.</strong></p>
              <p>
                Sorry! This probably means the link you followed is incorrect,
                because no factsheet ever existed with this particular ID. Your
                best bet is to go the homepage and create a new one for the area
                you were interested in, or get in touch via info@geofolio.org if
                you feel that an error has occurred.
              </p>
            </div>
            <div class="factsheet-status-error" v-if="factsheet_status == 403">
              <p>
                <strong
                  >You do not have the correct permissions to access this
                  factsheet.</strong
                >
              </p>
              <p>
                Factsheets created by registered users on Geofolio are private
                by default. If you created this factsheet while being logged in,
                you will have to log in again to be able to see it.
              </p>
            </div>
            <div class="factsheet-status-error" v-if="factsheet_status == 410">
              <p><strong>This factsheet has expired.</strong></p>
              <p>
                We clean up free factsheets and source data automatically data
                after 21 days to keep our hosting and operational expenses from
                becoming too large. You can generate a new one from the
                homepage.
              </p>
            </div>
            <div class="factsheet-status-error" v-if="factsheet_status == 500">
              <p><strong>An error occurred.</strong></p>
              <p>
                Apologies! An unexpected error occurred while generating this
                factsheet. This can occur when processing takes too long, when
                part of our infrastructure is down, or sometimes when data
                cannot be collected for a particular or unusual area of
                interest.
              </p>
              <p>
                Please try reloading this page in a few minutes just in case
                things are just taking longer than usual. If the problem
                persists you can e-mail us at support@geofolio.org and include a
                link to this page we can look into it for you.
              </p>
            </div>
            <div
              class="factsheet-status-ok"
              v-if="factsheet_status == 202"
              style="padding-bottom: 20px; margin: 50px 0px"
            >
              <p>
                <strong
                  >We are collecting data and assembling your factsheet. This
                  may take a few minutes, please be patient!</strong
                >
              </p>
              <p style="margin-bottom: 4px">{{ progressMessage }}</p>
              <div
                class="progress"
                style="margin-top: 0px; background-color: #fff"
              >
                <div
                  class="progress-bar bg-success"
                  role="progressbar"
                  :style="progressBarStyle"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Loading v-if="factsheet_status == null || !loaded" />

      <div class="container" v-if="false">
        <Settings title="Factsheet settings">
        Factsheet settings here (unlock, expiry, name, reprocessing, etc)
        </Settings>
      </div>

      <BasicFactsheet
        v-if="factsheetType == 'basic'"
        v-bind:location="location"
        v-bind:loaded="loaded"
        v-bind:factsheet="factsheet"
      />

      <LandscapeDiagnostic
        v-if="factsheetType == 'ldr'"
        v-bind:location="location"
        v-bind:loaded="loaded"
        v-bind:factsheet="factsheet"
      />

      <MountainFactsheet
        v-if="factsheetType == 'mountain'"
        v-bind:location="location"
        v-bind:loaded="loaded"
        v-bind:factsheet="factsheet"
      />

      <Embassy
        v-if="factsheetType == 'embassy'"
        v-bind:location="location"
        v-bind:loaded="loaded"
        v-bind:factsheet="factsheet"
      />
    </div>
    <Footer />
  </div>
</template>

<script>
/* eslint-disable */
import _ from "lodash";

import MainHeader from "@/components/generic/MainHeader.vue";
import SubHeader from "@/components/factsheet/SubHeader.vue";
import Footer from "@/components/generic/Footer.vue";

import Settings from "@/components/generic/ui/Settings.vue"

import BasicFactsheet from "@/views/factsheet/basic/Basic.vue";
import MountainFactsheet from "@/views/factsheet/mountain/Mountain.vue";
import LandscapeDiagnostic from "@/views/factsheet/ldr/LandscapeDiagnostic.vue";
import Embassy from "@/views/factsheet/embassy/Embassy.vue";

import Loading from "@/components/generic/ui/Loading.vue"

import omnivore from "@mapbox/leaflet-omnivore";

export default {
  name: "factsheet",
  components: {
    MainHeader,
    Settings,
    SubHeader,
    Footer,
    BasicFactsheet,
    LandscapeDiagnostic,
    MountainFactsheet,
    Embassy,
    Loading
  },
  data() {
    return {
      location_uuid: null,
      location_bounds: null,
      location: {
        layer: null,
        bounds: null,
        center: null,
      },
      factsheet: {
        identifier: this.$route.params.factsheet_id,
        data: {},
        info: null,
        filters: null,
        aois: null,
        dataset_paths: {},
        dataset_ids: {},
        baseUrl: null, // Set during create()
        //toolsUrl: process.env.VUE_APP_API+"/tools"
      },
      factsheet_id: this.$route.params.factsheet_id,
      factsheet_info: null,
      factsheet_status: null,
      loaded: false,
      datasetsLoaded: false,
      working: false,
      progress: null,
      progressMessage: null,
    };
  },
  computed: {
    baseUrl: function () {
      return (
        process.env.VUE_APP_API + "/data/factsheet/" + this.factsheet.identifier
      );
    },
    basePath: function () {
      return "/factsheet/" + this.factsheet.identifier;
    },
    factsheetApiUrl: function () {
      return (
        process.env.VUE_APP_API +
        "/api/factsheet/" +
        this.factsheet.identifier +
        "/"
      );
    },
    factsheetType: function () {
      if (this.loaded) {
        return this.factsheet["info"]["factsheettype"]["name"];
      } else {
        return null;
      }
    },
    progressBarStyle: function () {
      return "width:" + this.progress + "%";
    },
  },
  created() {
    /* 
        Load the main factsheet information via the factsheet API, and monitor
        this at intervals (and show updates to user) in case it's still 
        processing. Once loaded, set the factsheet information in the store 
        and display the factsheet.
         */
    this.monitorStatus();

    /* Overload the baseUrl to point to the S3 bucket corresponding to this
           deployment. */
    this.factsheet.baseUrl = this.baseUrl;
  },
  mounted() {},
  unmounted() {},
  watch: {
    loaded: function () {},
  },
  methods: {
    checkLoaded() {
      var filters_and_aois_loaded = false;
      if (this.factsheet.info != null) {
        if (this.factsheet.info["factsheettype"]["name"] == "ldr") {
          // If this is an ldr factsheet, ensure that aois and filters are loaded.
          if (this.factsheet.filters != null && this.factsheet.aois != null) {
            filters_and_aois_loaded = true;
          }
        } else {
          filters_and_aois_loaded = true;
        }
      }
      if (
        this.factsheet.info != null &&
        this.location.layer != null &&
        this.datasetsLoaded &&
        this.factsheet_status == "200" &&
        filters_and_aois_loaded == true
      ) {
        this.loaded = true;
      }
    },
    monitorStatus() {
      this.working = true;
      const headers = {
        Authorization: "Token " + this.apitoken(),
      };
      this.$axios
        .get(this.factsheetApiUrl, {
          headers: headers,
          validateStatus: () => true,
        })
        .then((response) => {
          this.factsheet_status = response.status;
          if (response.status == 200) {
            console.log(
              "Factsheet API returned 200. Load data and display the factsheet."
            );
            this.factsheet["info"] = response["data"];

            /* Load the different datasets by creating a bunch of promises to be fulfilled. */
            var promises = [];
            this.factsheet["info"]["datasets_latest"].forEach((dataset) => {
              /* Create promises */
              this.factsheet["data"][dataset["handler"]] = null;
              promises.push(
                this.$axios.get(
                  process.env.VUE_APP_API +
                    "/data/factsheet" +
                    dataset["path"] +
                    "/" +
                    dataset["handler"] +
                    ".json"
                )
              );

              /* 
                        Fill the dataset_ids property on the factsheet so what everywhere that we have access to the factsheet we can also determine the id of a particular
                        dataset.
                        */
              this.factsheet.dataset_paths[dataset["handler"]] =
                dataset["path"];
              this.factsheet.dataset_ids[dataset["handler"]] = dataset["id"];

              /* 
                        If the dataset is called 'geofolio_ldr_aoi' then load a special file called 
                        filters.json and aois.json, which are GeoJSON files containing filters and 
                        custom or automatically generated areas of interest for the LDR reports.

                        If these files are not found this is no big deal, just set the filters and 
                        aois to an empty list and the report should be able to deal with it accordingly.
                        */
              if (dataset["handler"] == "geofolio_ldr_aoi") {
                /* Filters */
                this.$axios
                  .get(
                    process.env.VUE_APP_API +
                      "/data/factsheet" +
                      dataset["path"] +
                      "/filters.json"
                  )
                  .then((response) => {
                    if (response.status == 200) {
                      this.factsheet["filters"] = response.data;
                      console.log("Filters json loaded.");
                    } else {
                      console.log("Filters json not found.");
                      this.factsheet["filters"] = [];
                    }
                    this.checkLoaded();
                  })
                  .catch((err) => {
                    console.log("Filters json not found.");
                    this.factsheet["filters"] = [];
                  });
                /* Areas of interest. */
                this.$axios
                  .get(
                    process.env.VUE_APP_API +
                      "/data/factsheet" +
                      dataset["path"] +
                      "/aois.json"
                  )
                  .then((response) => {
                    if (response.status == 200) {
                      this.factsheet["aois"] = response.data;
                      console.log("Aois json loaded.");
                    } else {
                      console.log("Aois json not found.");
                      this.factsheet["aois"] = [];
                    }
                    this.checkLoaded();
                  })
                  .catch((err) => {
                    console.log("Aois json not found.");
                    this.factsheet["aois"] = [];
                  });
              }
            });

            Promise.allSettled(promises).then((results) => {
              results.forEach((result) => {
                if (result["status"] == "fulfilled") {
                  _.extend(this.factsheet.data, result["value"]["data"]);
                } else {
                  console.log(
                    "Failed to load dataset url: " +
                      result["reason"]["config"]["url"]
                  );
                }
              });
              this.datasetsLoaded = true;
              this.checkLoaded();
            });

            this.$axios.get(this.baseUrl + "/location.wkt").then((response) => {
              var customLayer = L.geoJson(null, {
                interactive: false,
                style: function (feature) {
                  return {
                    color: "#e50000",
                    weight: 3,
                    opacity: 1,
                    fillOpacity: 0,
                  };
                },
              });
              var layer = omnivore.wkt.parse(response.data, null, customLayer);
              this.location = {
                layer: layer,
                wkt_geom: response.data,
                bounds: layer.getBounds(),
                center: layer.getBounds().getCenter(),
              };
              this.checkLoaded();
            });

            this.checkLoaded();
          }
          if (response.status == 202) {
            console.log(
              "Factsheet API returned 202. Still processing. Checking status in again in 2s."
            );
            this.progress = Math.max(1, response.data["percent_complete"]);
            this.progressMessage = response.data["progress_message"];
            setTimeout(
              function () {
                this.monitorStatus();
              }.bind(this),
              2000
            );
          }
          if (response.status == 404) {
            console.log(
              "Factsheet API returned 404. Factsheet does not exist and has not existed."
            );
          }
          if (response.status == 403) {
            console.log(
              "Factsheet API returned 403. No permission to view this factsheet."
            );
          }
          if (response.status == 410) {
            console.log(
              "Factsheet API returned 410. Probably an old factsheet URL from before recent upgrades."
            );
          }
        });
    },
  },
};
</script>

<style>
body {
  color: #666;
}
div.factsheet {
  font-size: 1.5em;
}
div.factsheet div.container {
  background-color:#fff;
  border-left:1px solid #ddd;
  border-right:1px solid #ddd;
}
div.container {
  margin-bottom:0px;
  margin-top:0px;
}
div.factsheet h1 {
  font-size: 1.3em;
  font-weight: 600;
  padding-top: 15px;
  margin-top:0px;
}

div.factsheet h2 {
  font-size: 1.2em;
  font-weight: 600;
  padding-top: 15px;
  margin-top:0px;
}

div.factsheet h3 {
  font-size: 1.05em;
  font-weight: 600;
  margin-top: 15px;
}

div.factsheet p {
  line-height: 1.4em;
}

div.factsheet ul li {
  padding: 7px 0px 7px 0px;
  line-height: 1.4em;
}

ul#toc li {
  line-height: 1em !important;
}

div.factsheet table td {
  color: #666;
  line-height: 1.4em;
}

ul.collapsed {
  list-style-type: none;
  margin-left: 0px;
  padding-left: 0px;
  margin-bottom: 5px;
}
ul.collapsed li {
  margin-left: 0px;
}

div.data-availability ul {
  list-style-type: none;
  margin-left: 0px;
  padding-left: 0px;
  margin-bottom: 5px;
}
div.data-availability ul li {
  margin-left: 0px;
}
div.factsheet-status-error {
  padding: 20px 20px 6px 20px;
  font-size: 0.8em;
  border-radius: 0px;
  color: #e50000;
  border: 2px solid #e50000;
  background-color: #fff0f0;
}
div.factsheet-status-ok {
  padding: 20px 20px 6px 20px;
  font-size: 0.8em;
  border-radius: 0px;
  color: #378c07;
  border: 2px solid #15b01a;
  background-color: #e8fae9;
}
div.factsheet-status-notice {
  padding: 20px 20px 6px 20px;
  font-size: 0.8em;
  border-radius: 0px;
  color: #666;
  border: 2px solid #ffd152;
  background-color: #ffffe4;
}
</style>
